import { useCallback, useEffect, useState } from "react";
import { LogOut } from "lucide-react";
import { Toaster } from "@/components/ui/sonner";
import { apiFetch } from "./api";
import { Login } from "./Login";
import { Leads } from "./Leads";
import { ContentEditor } from "./ContentEditor";
import { Settings } from "./Settings";
import { Analytics } from "./Analytics";
import { Proformas } from "./Proformas";
import { Blog } from "./Blog";

const TOKEN_KEY = "as_admin_token";

const TABS = [
    { id: "analytics", label: "Analytics" },
    { id: "leads", label: "Dealer Leads" },
    { id: "proformas", label: "Saved Proformas" },
    { id: "blog", label: "Blog" },
    { id: "content", label: "Page Content" },
    { id: "settings", label: "Settings" },
];

export const AdminApp = () => {
    const [token, setToken] = useState(
        () => localStorage.getItem(TOKEN_KEY) || "",
    );
    const [user, setUser] = useState(null);
    const [checking, setChecking] = useState(!!localStorage.getItem(TOKEN_KEY));
    const [tab, setTab] = useState("analytics");

    useEffect(() => {
        document.title = "AdvantaShield — Admin";
        let meta = document.head.querySelector('meta[name="robots"]');
        if (!meta) {
            meta = document.createElement("meta");
            meta.setAttribute("name", "robots");
            document.head.appendChild(meta);
        }
        meta.setAttribute("content", "noindex, nofollow");
    }, []);

    const logout = useCallback(() => {
        localStorage.removeItem(TOKEN_KEY);
        setToken("");
        setUser(null);
    }, []);

    useEffect(() => {
        if (!token) return;
        apiFetch("/auth/me", { token })
            .then(setUser)
            .catch(() => logout())
            .finally(() => setChecking(false));
    }, [token, logout]);

    const onLogin = (newToken, newUser) => {
        localStorage.setItem(TOKEN_KEY, newToken);
        setToken(newToken);
        setUser(newUser);
    };

    if (checking) {
        return (
            <div className="flex min-h-screen items-center justify-center bg-[#0A0D14] text-sm text-slate-500">
                Checking session…
            </div>
        );
    }

    if (!token || !user) {
        return (
            <>
                <Login onSuccess={onLogin} />
                <Toaster position="top-center" richColors />
            </>
        );
    }

    return (
        <div data-testid="admin-dashboard" className="min-h-screen bg-[#0A0D14]">
            <header className="border-b border-white/10 bg-[#0C1019]">
                <div className="mx-auto flex h-16 max-w-6xl items-center justify-between px-4 sm:px-6">
                    <div className="flex items-center gap-3">
                        <img
                            src="/logo-horizontal.webp"
                            alt="AdvantaShield Warranty"
                            width="440"
                            height="146"
                            decoding="async"
                            className="h-8 w-auto"
                        />
                        <span className="rounded-full border border-sky-400/30 bg-sky-400/10 px-3 py-1 font-mono text-[10px] font-semibold uppercase tracking-[0.2em] text-sky-300">
                            Admin
                        </span>
                    </div>
                    <div className="flex items-center gap-4">
                        <span className="hidden text-xs text-slate-500 sm:block">
                            {user.email}
                        </span>
                        <button
                            data-testid="admin-logout-btn"
                            onClick={logout}
                            className="inline-flex items-center gap-2 rounded-full border border-white/15 px-4 py-2 text-xs font-semibold text-white transition-colors hover:border-red-400/50 hover:text-red-300"
                        >
                            <LogOut className="h-3.5 w-3.5" /> Sign out
                        </button>
                    </div>
                </div>
            </header>

            <div className="mx-auto max-w-6xl px-4 py-8 sm:px-6">
                <nav className="flex gap-2 overflow-x-auto">
                    {TABS.map((t) => (
                        <button
                            key={t.id}
                            data-testid={`admin-tab-${t.id}`}
                            onClick={() => setTab(t.id)}
                            className={`rounded-full px-5 py-2.5 text-sm font-semibold transition-colors duration-300 ${
                                tab === t.id
                                    ? "bg-sky-400 text-[#0A0D14]"
                                    : "border border-white/15 text-slate-300 hover:border-sky-400/40 hover:text-white"
                            }`}
                        >
                            {t.label}
                        </button>
                    ))}
                </nav>

                <div className="mt-8">
                    {tab === "analytics" && (
                        <Analytics token={token} onAuthError={logout} />
                    )}
                    {tab === "leads" && (
                        <Leads token={token} onAuthError={logout} />
                    )}
                    {tab === "proformas" && (
                        <Proformas token={token} onAuthError={logout} />
                    )}
                    {tab === "blog" && (
                        <Blog token={token} onAuthError={logout} />
                    )}
                    {tab === "content" && (
                        <ContentEditor token={token} onAuthError={logout} />
                    )}
                    {tab === "settings" && (
                        <Settings token={token} onAuthError={logout} />
                    )}
                </div>
            </div>
            <Toaster position="top-center" richColors />
        </div>
    );
};
